import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getDevelcoGatewaySensors,
  DeviceType,
} from "@intelligentlilli/api-layer";
import { setSensors } from "../../State/slices/sensors-slice";
import { INITAL_SENSORS } from "../Constants";
import * as Sentry from "@sentry/react";

type GetSensorStatusProps = {
  devicestring: string | undefined;
  id: string | undefined;
  continuous?: boolean;
};

export const useGetSensorStatus = ({
  devicestring,
  id,
  continuous = false,
}: GetSensorStatusProps) => {
  // redux state
  const dispatch = useDispatch();
  const server = useSelector((state) => state.session.server);
  const sensors = useSelector((state) => state.sensors) || INITAL_SENSORS;
  const user = useSelector((state) => state.user.authData);

  // local state
  const [error, setError] = useState<string | false>(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DeviceType[]>();

  const hubIsOnline = sensors?.hub?.online;

  // Create a callback function that fetches the sensor status from a device
  const fetchSensorStatus = useCallback(
    (callback?: () => void) => {
      setLoading(true);
      setError(false);
      return getDevelcoGatewaySensors(server, devicestring || "", "web")
        .then((res) => {
          if (res.ok && res.data?.data) {
            setLoading(false);
            setData(res.data?.data);
            typeof callback === "function" && callback();
          } else {
            console.log(
              "getDevelcoGatewaySensors NOT OK, status:",
              res.status,
              "res.data:",
              res?.data
            );
            setLoading(false);
            setError("Could not detect sensors.");
          }
        })
        .catch((err) => {
          console.log("getDevelcoGatewaySensors ERROR  block:", err);
          setLoading(false);
          setError("Sorry, we could not connect the sensor(s) to the hub.");
          Sentry.withScope(function (scope) {
            scope.setTag('Install-API', 'getDevelcoGatewaySensors');
            Sentry.captureException(
              new Error(
                `WEB - getDevelcoGatewaySensors - resident ID: ${id}, userId: ${user.key}, status: caught error: ${err}- at ${new Date().toISOString()}`
              )
            );
          });
        });
    },
    [devicestring, server, id, user.key]
  );

  // Store fetchSensorStatus and hubIsOnline in refs so the polling effect
  // doesn't re-run (and create duplicate chains) when their values change.
  const fetchRef = useRef(fetchSensorStatus);
  fetchRef.current = fetchSensorStatus;
  const hubIsOnlineRef = useRef(hubIsOnline);
  hubIsOnlineRef.current = hubIsOnline;

  // Upon load fetch the sensor status, then poll every 15s using a setTimeout
  // chain so the next request only starts after the previous one completes.
  // Only depends on `continuous` so the effect starts once and stops once.
  useEffect(() => {
    if (!continuous) return;

    let cancelled = false;

    const poll = () => {
      // use '.finally' instead of '.then' so that the poll continues even
      // in the event that there is a caught error
      fetchRef.current().finally(() => {
        if (!cancelled && !hubIsOnlineRef.current) {
          timeoutId = window.setTimeout(poll, 15000); // 15 seconds
        }
      });
    };

    let timeoutId: number | undefined;
    poll();

    // Clean up when the effect is re-run or unmounted
    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [continuous]);

  // Hook listening for changes in the data and persisting them to state
  useEffect(() => {
    // Sometimes the response comes back as updating the hub. Ignore these responses as they give no information
    if (data && data.length > 0 && data[0]?.name !== "update") {
      // Save the results in redux
      console.log("-- dispatching new sensor results -- ");
      dispatch(
        setSensors({
          devices: data,
          hubId: id,
          devicestring: devicestring,
        })
      );
    }
  }, [data, dispatch, id, devicestring]);

  return { loading, fetchSensorStatus, error, hubIsOnline, sensors };
};
